import { useState, useEffect, useCallback } from 'react';
import { API_ENDPOINTS } from '../config/api';
import { useAuth } from '../context/AuthContext';

export const useMCPStocks = () => {
  const { user } = useAuth();
  const [stocks, setStocks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Call MCP server
  const callMCPServer = async (command, params = []) => {
    if (!user?.uid) {
      throw new Error('User must be authenticated to use MCP commands');
    }
    
    try {
      const response = await fetch(API_ENDPOINTS.MCP, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          command: `${command} ${params.join(' ')}`.trim(),
          userId: user.uid
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`MCP Server error: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('MCP Server error:', error);
      throw error;
    }
  };

  // Load stocks from MCP server
  const loadStocks = useCallback(async () => {
    if (!user) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const result = await callMCPServer('list_stocks');
      if (result.status === 'success') {
        setStocks(result.stocks || []);
      } else {
        throw new Error(result.message || 'Failed to load stocks');
      }
    } catch (err) {
      setError(err.message);
      console.error('Error loading stocks:', err);
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Add stock via MCP server
  const addStock = async (stockData) => {
    if (!user) return null;
    
    try {
      const result = await callMCPServer('add_stock', [stockData.symbol]);
      if (result.status === 'success') {
        await loadStocks();
        return result.stock;
      } else {
        throw new Error(result.message || 'Failed to add stock');
      }
    } catch (err) {
      setError(err.message);
      console.error('Error adding stock:', err);
      throw err;
    }
  };

  // Update stock via MCP server
  const updateStock = async (id, updates) => {
    if (!user) return null;
    
    try {
      const updateString = Object.entries(updates)
        .map(([key, value]) => `${key}:${value}`)
        .join(' ');
      
      const result = await callMCPServer('update_stock', [id, updateString]);
      
      if (result.status === 'success') {
        await loadStocks();
        return result.stock;
      } else {
        throw new Error(result.message || 'Failed to update stock');
      }
    } catch (err) {
      setError(err.message);
      console.error('Error updating stock:', err);
      throw err;
    }
  };

  // Delete stock via MCP server
  const deleteStock = async (id) => {
    if (!user) return;
    
    try {
      const result = await callMCPServer('delete_stock', [id]);
      if (result.status === 'success') {
        await loadStocks();
      } else {
        throw new Error(result.message || 'Failed to delete stock');
      }
    } catch (err) {
      setError(err.message);
      console.error('Error deleting stock:', err);
      throw err;
    }
  };

  // Load stocks when user changes
  useEffect(() => {
    if (user) {
      loadStocks();
    } else {
      setStocks([]);
    }
  }, [user, loadStocks]);

  return {
    stocks,
    loading,
    error,
    addStock,
    updateStock,
    deleteStock,
    loadStocks,
    setError
  };
};
